// service for handling the webrtc streaming
// orchestrate the signaling and peer connection services
import { Injectable } from '@angular/core';
import { RequestWebRTCLivestreamResp, SocketIOConfig } from '@app-core/models/livestream';
import { Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

// currently the architecture is not 100% reliable as signaling channel can go down.
// events are not handled for that case

// todo - configure different services for signaling and handling peer connection

@Injectable({
  providedIn: 'root',
})
export class WebRTcStreamingService {
  public remoteTrack = new Subject();
  public remoteMedia = new MediaStream();
  public connectionState = new Subject();
  private socket: Socket;
  private peerConnection: RTCPeerConnection;
  private _signalingChannelState = new Subject();
  private _iceCandidates = new Subject<RTCIceCandidate>();
  private _answer = new Subject<RTCSessionDescriptionInit>();
  private streamingDetails: any;
  private interval;
  constructor() {
    // handle signalling state changes
    this._signalingChannelState.subscribe((state) => {
      switch (state) {
        case 'connected': {
          // todo - remove in production
          console.debug('socket connected');
          break;
        }
        case 'disconnected': {
          // todo - remove in production
          console.debug('socket disconenected');
          break;
        }
        case 'fail': {
          // todo - remove in production
          console.debug('socket failed');
          break;
        }
      }
    });

    this._iceCandidates.subscribe({
      next: (candidate: RTCIceCandidate) => {
        // instead of sending the signal to the be immediately, ensure that the signalling channel is active and running
        // if the channel is down, then wait for it to become active before sending it.
        // this can be handled using observables
        if (this.socket.connected) {
          this.socket.emit('message', { type: 'ice-candidate', payload: candidate });
        }
      },
    });

    this._answer.subscribe({
      next: (offer: RTCSessionDescriptionInit) => {
        this.socket.emit('message', { type: 'answer', payload: offer });
      },
    });
  }

  public initializeStreaming(streamingDetails: RequestWebRTCLivestreamResp) {
    this.streamingDetails = streamingDetails;
    this.initSocket(streamingDetails.socketIOConfig);
    this.initializePeerConnection(this.streamingDetails.iceServers);
    this.initializeSocketEvent();
    this.interval = setInterval(() => {
      this.generateStats();
    }, 5000);
  }

  private initializePeerConnection(iceServer: RTCIceServer) {
    const config: RTCConfiguration = {
      iceServers: [iceServer],
    };

    if (!this.peerConnection) {
      this.peerConnection = new RTCPeerConnection(config);
    } else {
      this.peerConnection.restartIce();
    }

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this._iceCandidates.next(event.candidate);
      }
    };
    this.peerConnection.ontrack = (event) => {
      this.remoteMedia.addTrack(event.track);
      this.remoteTrack.next(event.track);
    };
  }

  private initializeSocketEvent() {
    this.socket.on('message', (message: any) => {
      switch (message.type) {
        case 'offer': {
          this.peerConnection.setRemoteDescription(new RTCSessionDescription(message.payload)).then(() => {
            this.peerConnection.createAnswer().then((answer) => {
              this.peerConnection.setLocalDescription(answer).then(() => {
                this._answer.next(answer);
              });
            });
          });

          break;
        }
        case 'ice-candidate': {
          this.peerConnection.addIceCandidate(new RTCIceCandidate(message.payload));
          break;
        }
        case 'device-connected': {
          this.connectionState.next('device-connected');
          break;
        }
      }
      // Handle incoming offer
    });
    this.socket.on('connect', () => this._signalingChannelState.next('connect'));
    this.socket.on('disconnect', () => this._signalingChannelState.next('disconnect'));
    this.socket.on('fail', (error: any) => {
      // todo - remove in production
      console.debug('fail:', error);
      this._signalingChannelState.next('fail');
    });
    this.socket.on('connect_error', (error: any) => {
      // todo - remove in production
      console.error('connect_error', error);
      this._signalingChannelState.next('connect_error');
    });
    this.socket.on('reconnect_failed', (error: any) => {
      // todo - remove in production
      console.error('reconnect_failed', error);
      this._signalingChannelState.next('reconnect_failed');
    });
  }

  private initSocket(config: SocketIOConfig) {
    this.socket = io(config.endpoint, {
      path: config.path,
      transports: ['websocket'],
      auth: {
        token: config.token,
      },
    });
  }

  private generateStats() {
    if (this.peerConnection) {
      this.peerConnection.getStats(null).then((stats) => {
        stats.forEach((report) => {
          console.log(report.id, report.timestamp, report.type);
          console.log(report);
        });
      });
    }
  }
  public stopStreaming() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.removeAllListeners();
      this.socket.off();
      this.socket = null;
    }
    if (this.peerConnection) {
      if (this.interval) clearInterval(this.interval);
      this.peerConnection.close();
      this.peerConnection = null;
    }
  }
}
